import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=anonymous, object=authed
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    const boot = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setBootstrapped(true);
      }
    };
    boot();
  }, []);

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) localStorage.setItem("lms_token", data.access_token);
      // Fetch full profile (including phone, etc.) — login response is intentionally minimal.
      const full = await refreshUser();
      if (!full) setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("lms_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, bootstrapped, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

/**
 * Normalize role string so UI RBAC checks are robust to legacy DB values like
 * 'Sales Manager', ' sales_manager ', 'sales-manager'.
 * Mirrors backend/auth.py::normalize_role. Keep in sync.
 */
export function normalizeRole(role) {
  if (!role) return "viewer";
  return String(role).trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function hasRole(user, ...allowed) {
  if (!user) return false;
  const role = normalizeRole(user.role);
  if (role === "super_admin") return true;
  const norm = allowed.map(normalizeRole);
  return norm.includes(role);
}
